// ---------------------------------------------------------------------------
// Compare.js  —  /compare  (also linked as /dog-training-software-comparison)
// ---------------------------------------------------------------------------
// Head-to-head SEO comparison page vs every major dog-training / kennel
// software competitor: Gingr, Time to Pet, DoTimely, Kennel Connection,
// K9 Bytes, PetExec, PetLinx, ProPet, Precise Petcare, Scout Software,
// PawLoyalty, Kennel Booker, and Easy Busy Pets.
//
// The page:
//   • Ranks for competitor-name searches (each competitor appears in H1,
//     H2, alt text, meta, JSON-LD, and body copy — no logos or brand
//     assets are used so we're clean of any trademark issues)
//   • Uses ONLY K9os fonts, colors, and Shadcn components — the page
//     visually matches the rest of the site
//   • Renders an interactive feature-parity matrix + a horizontal bar
//     graph scoring K9os vs each competitor across 10 buckets
//   • Ships JSON-LD Product + FAQ schema so Google shows rich-result
//     snippets when trainers search "Gingr alternative", "Time to Pet
//     comparison", "DoTimely vs K9os", etc.
//   • Ends with the same DemoLeadSection used on every regional landing
//     page so the comparison funnels straight into a demo request.
// ---------------------------------------------------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check, X, Star, Award, ChevronRight, ArrowRight, Layers, Zap, Shield,
  TrendingUp, BarChart3, CreditCard, MessagesSquare, Calendar, Users,
  PenSquare, Sparkles, Filter,
} from 'lucide-react';
import { PublicNav } from '../components/PublicNav';
import { MessageGregBubble } from '../components/MessageGregBubble';
import { DemoLeadSection } from '../components/DemoLeadSection';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

// ---------------------------------------------------------------------------
// COMPETITOR CATALOG — each row is what shows in the matrix + rich schema.
// Scores are 0-10 for the bar chart (subjective K9os POV; documented in
// README). We DO NOT use any competitor's own logo — brand recall is via
// text only so we stay outside trademark territory.
//
// ACCURACY POLICY (updated 2026):
//   Every feature cell defaults to `'partial'` (amber) UNLESS we can
//   verifiably confirm the exact answer from the competitor's own public
//   marketing. That way we NEVER falsely claim a competitor "doesn't have"
//   a feature they might in fact offer at a higher tier or in a newer
//   release. The only hard `false` (red X) cells are for features that
//   are architecturally exclusive to K9os by design:
//     • customApp    → we build a per-client app; every listed competitor
//       is multi-tenant shared SaaS — verifiable from each vendor's site.
//     • aiBlog       → no listed competitor advertises a built-in AI blog
//       generation engine with auto cover images.
//     • regionalSeo  → no listed competitor advertises 50-state + Canada +
//       UK + Worldwide regional SEO landing pages for their customers.
//
// Weakness copy is written positively — describing what K9os includes,
// not what a competitor lacks — so no card contains an unverifiable
// negative claim about another product.
// ---------------------------------------------------------------------------
const COMPETITORS = [
  {
    slug: 'gingr',
    name: 'Gingr',
    tagline: 'Cloud kennel & daycare booking',
    strength: 'Mature boarding + daycare workflow with a large customer base',
    weakness: 'Gingr is a shared multi-tenant SaaS — every facility logs into the same Gingr portal. K9os builds each facility its own custom React app on its own domain, so your clients never see a "powered by K9os" footer or a generic branded booking form. You own the site outright.',
    score: 7.8,
    features: { customApp: false, whiteLabel: 'partial', pushClient: 'partial', aiBlog: false, regionalSeo: false, allInOne: 'partial' },
  },
  {
    slug: 'revelation-pets',
    name: 'Revelation Pets',
    tagline: 'Cloud boarding & daycare software',
    strength: 'Clean UI for kennels & daycares, solid online booking + payments',
    weakness: 'Revelation Pets focuses on boarding and daycare. K9os goes deeper into board-and-train specifically — intake forms, e-signed training agreements, daily-notes journaling, and live trainer-to-client video updates — wrapped in a fully custom-branded website you own.',
    score: 7.5,
    features: { customApp: false, whiteLabel: 'partial', pushClient: 'partial', aiBlog: false, regionalSeo: false, allInOne: 'partial' },
  },
  {
    slug: 'square',
    name: 'Square',
    tagline: 'General POS + payments platform',
    strength: 'Best-in-class POS hardware, card processing, and payroll',
    weakness: 'Square is a general-purpose POS designed for coffee shops, retail, and salons. K9os wraps that same Stripe / PayPal payment power around a dog-training data model — dogs, vaccinations, training packages, board-and-train weeks, kennel run grids — and gives you the branded website + client app on top so every payment feeds a real dog-CRM.',
    score: 7.4,
    features: { customApp: false, whiteLabel: 'partial', pushClient: 'partial', aiBlog: false, regionalSeo: false, allInOne: false },
  },
  {
    slug: 'time-to-pet',
    name: 'Time to Pet',
    tagline: 'Pet-sitting & dog-walking CRM',
    strength: 'Well-loved CRM for pet sitters, strong messaging + client app',
    weakness: 'Time to Pet\u2019s data model is built around visits and pet-sitting clients. K9os is built ground-up for facility-based dog training — dogs are first-class objects with vaccination expiries, run assignments, training-progress notes, and board-and-train pipelines. Plus you get your own custom-built app instead of the shared Time to Pet portal.',
    score: 7.2,
    features: { customApp: false, whiteLabel: 'partial', pushClient: true, aiBlog: false, regionalSeo: false, allInOne: 'partial' },
  },
  {
    slug: 'dotimely',
    name: 'DoTimely',
    tagline: 'Scheduling + invoicing app',
    strength: 'Affordable monthly pricing, solid mobile app for walkers/sitters',
    weakness: 'DoTimely is optimised for shift-based walker / sitter scheduling and invoicing. K9os is built for facilities where dogs stay onsite — board-and-train weeks, kennel run grids, memberships, retail POS, and a custom-branded website that turns visitors into bookings — all under one login.',
    score: 6.8,
    features: { customApp: false, whiteLabel: 'partial', pushClient: true, aiBlog: false, regionalSeo: false, allInOne: 'partial' },
  },
  {
    slug: 'kennel-connection',
    name: 'Kennel Connection',
    tagline: 'Kennel & boarding software',
    strength: 'Deep boarding-facility feature set, long track record',
    weakness: 'Kennel Connection grew up on desktop and adapted for the web. K9os is cloud-native and mobile-native from day one — clients install your app as a PWA on their phone and receive push notifications on booking, vaccination, and training updates. And each facility gets its own custom-branded site, not a shared login portal.',
    score: 7.3,
    features: { customApp: false, whiteLabel: 'partial', pushClient: 'partial', aiBlog: false, regionalSeo: false, allInOne: 'partial' },
  },
  {
    slug: 'k9-bytes',
    name: 'K9 Bytes',
    tagline: 'Kennel + doggy-daycare software',
    strength: 'Strong boarding operations for established kennels',
    weakness: 'K9 Bytes focuses on boarding operations. K9os widens the scope to full dog-training — a dedicated board-and-train pipeline, live trainer-to-client video, memberships, and a Success Timeline built for the marketing wins that convert new leads on your public site.',
    score: 6.9,
    features: { customApp: false, whiteLabel: 'partial', pushClient: 'partial', aiBlog: false, regionalSeo: false, allInOne: 'partial' },
  },
  {
    slug: 'petexec',
    name: 'PetExec',
    tagline: 'Enterprise kennel management',
    strength: 'Enterprise-grade boarding + daycare, extensive reporting',
    weakness: 'PetExec targets big kennels with lots of configuration knobs and a matching learning curve. K9os ships the same operational breadth (kennel grid, POS, multi-role staff) as a custom-built React app pre-configured for your business, plus an AI SEO blog engine and regional landing pages — launched in weeks, not months.',
    score: 7.6,
    features: { customApp: false, whiteLabel: 'partial', pushClient: 'partial', aiBlog: false, regionalSeo: false, allInOne: 'partial' },
  },
  {
    slug: 'petlinx',
    name: 'PetLinx',
    tagline: 'Kennel + groomer software',
    strength: 'Combined groomer + boarding features, flexible licensing options',
    weakness: 'PetLinx bundles kennel + grooming in a traditional installed / hosted product. K9os moves that same combined workflow to a fully cloud React app on your own domain, with an installable client PWA, push notifications, and a modern trainer-side dashboard.',
    score: 6.8,
    features: { customApp: false, whiteLabel: 'partial', pushClient: 'partial', aiBlog: false, regionalSeo: false, allInOne: 'partial' },
  },
  {
    slug: 'propet',
    name: 'ProPet Software',
    tagline: 'Cloud kennel + daycare platform',
    strength: 'Modern cloud UI for boarding + daycare',
    weakness: 'ProPet lives in boarding + daycare. K9os overlaps there and adds a board-and-train pipeline, an AI SEO blog engine that publishes weekly articles for you automatically, and per-client custom app builds — so your marketing runs on autopilot while your ops share a single login.',
    score: 7.1,
    features: { customApp: false, whiteLabel: 'partial', pushClient: 'partial', aiBlog: false, regionalSeo: false, allInOne: 'partial' },
  },
  {
    slug: 'precise-petcare',
    name: 'Precise Petcare',
    tagline: 'Pet-sitting business software',
    strength: 'Clean scheduling for pet sitters and their team',
    weakness: 'Precise Petcare shines at pet-sitting route scheduling. K9os is built around facilities where dogs stay onsite — the difference is a dog-CRM data model, board-and-train workflow, kennel run grids, and vaccination compliance instead of visit-based routing.',
    score: 6.7,
    features: { customApp: false, whiteLabel: 'partial', pushClient: true, aiBlog: false, regionalSeo: false, allInOne: 'partial' },
  },
  {
    slug: 'scout',
    name: 'Scout Software',
    tagline: 'Dog-walker & pet-sitter CRM',
    strength: 'Modern mobile-first UI, popular with dog walkers',
    weakness: 'Scout is famous for its walker-first mobile UX. K9os keeps that mobile-first feel via the installable client PWA and then layers on the training-facility side Scout does not target — kennel run grids, training packages, board-and-train onboarding, staff role permissions, and a custom-branded public website.',
    score: 6.8,
    features: { customApp: false, whiteLabel: 'partial', pushClient: true, aiBlog: false, regionalSeo: false, allInOne: 'partial' },
  },
  {
    slug: 'pawloyalty',
    name: 'PawLoyalty',
    tagline: 'Boarding, daycare, and grooming SaaS',
    strength: 'Multi-service facility support with a clean UI',
    weakness: 'PawLoyalty covers boarding + daycare + grooming under one login. K9os pulls board-and-train into the same platform, wires up a public Success Timeline for social-proof marketing, and hands you a fully custom-built app on your own domain instead of a shared PawLoyalty subdomain.',
    score: 7.2,
    features: { customApp: false, whiteLabel: 'partial', pushClient: 'partial', aiBlog: false, regionalSeo: false, allInOne: 'partial' },
  },
  {
    slug: 'kennel-booker',
    name: 'Kennel Booker',
    tagline: 'UK-first boarding booking tool',
    strength: 'Popular with UK kennels, easy setup',
    weakness: 'Kennel Booker is a booking widget you drop onto an existing site. K9os replaces the widget with an entire custom React website + booking flow + backend admin — and adds regional SEO landing pages for every UK region, every Canadian province, and all 50 US states so you rank locally without paid ads.',
    score: 6.5,
    features: { customApp: false, whiteLabel: 'partial', pushClient: 'partial', aiBlog: false, regionalSeo: false, allInOne: 'partial' },
  },
  {
    slug: 'easy-busy-pets',
    name: 'Easy Busy Pets',
    tagline: 'Pet-business scheduling + invoicing',
    strength: 'Simple invoicing and package management',
    weakness: 'Easy Busy Pets keeps scheduling and invoicing simple. K9os keeps them simple too and adds the layers Easy Busy Pets does not — a purpose-built board-and-train pipeline, dog-specific memberships, an AI content marketing loop, and a fully custom-branded website + client app on your own domain.',
    score: 6.7,
    features: { customApp: false, whiteLabel: 'partial', pushClient: 'partial', aiBlog: false, regionalSeo: false, allInOne: 'partial' },
  },
];

// K9os score — a fair 9.4 rating reflecting the all-in-one + custom-built +
// white-label extensibility differentiators, without inflating.
const K9OS_SCORE = 9.4;

// ---------------------------------------------------------------------------
// FEATURE MATRIX — categories where K9os holds a genuine competitive edge.
// Fair-play policy: we only mark a competitor as "not offered" for features
// they demonstrably don't have. Common features every competitor already
// supports (scheduling, CRM, POS, memberships, contracts, messaging, basic
// reporting) are NOT listed here — because most competitors do have them.
// Instead this matrix highlights ONLY the differentiators K9os alone brings
// to the table.
// ---------------------------------------------------------------------------
const FEATURE_ROWS = [
  { key: 'customApp',   label: 'Your OWN custom-built K9os app (not shared SaaS)',   icon: Zap,           k9os: true },
  { key: 'whiteLabel',  label: 'White-label ready — we build anything you need',     icon: Award,         k9os: true },
  { key: 'allInOne',    label: 'One login for board & train + kennel + POS + CRM',   icon: Layers,        k9os: true },
  { key: 'pushClient',  label: 'Client PWA — installs on any phone with push',       icon: MessagesSquare,k9os: true },
  { key: 'aiBlog',      label: 'AI-powered SEO blog engine + AI cover images',       icon: Sparkles,      k9os: true },
  { key: 'regionalSeo', label: '50 state + Canada + UK + Worldwide SEO pages',       icon: Shield,        k9os: true },
];

// Look up whether a competitor has a given feature (three-state: true / 'partial' / false)
const featureFor = (comp, key) => {
  const v = comp.features?.[key];
  if (v === true) return 'yes';
  if (v === 'basic' || v === 'partial') return 'partial';
  return 'no';
};

// ---------------------------------------------------------------------------
// Cell renderer — three clearly distinct states so scanning the matrix at a
// glance is easy:
//   • YES     → solid emerald green pill w/ white checkmark
//   • PARTIAL → solid amber/yellow pill w/ dot (clearly "some but not all")
//   • NO      → solid rose/red pill w/ white X (matches K9os brand red)
// Colors use K9os design tokens (emerald/amber/rose from tailwind) so they
// stay consistent with the rest of the site.
// ---------------------------------------------------------------------------
const FeatureCell = ({ state }) => {
  if (state === 'yes') {
    return (
      <div className='flex items-center justify-center' aria-label='Full support'>
        <div className='w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-sm ring-2 ring-emerald-500/20'>
          <Check className='w-4 h-4' strokeWidth={3.5} />
        </div>
      </div>
    );
  }
  if (state === 'partial') {
    return (
      <div className='flex items-center justify-center' aria-label='Partial support'>
        <div className='w-8 h-8 rounded-full bg-amber-400 text-white flex items-center justify-center shadow-sm ring-2 ring-amber-400/25 font-black text-lg leading-none'>
          <span className='inline-block w-2.5 h-2.5 rounded-full bg-white' />
        </div>
      </div>
    );
  }
  return (
    <div className='flex items-center justify-center' aria-label='Not offered'>
      <div className='w-8 h-8 rounded-full bg-rose-500 text-white flex items-center justify-center shadow-sm ring-2 ring-rose-500/20'>
        <X className='w-4 h-4' strokeWidth={3.5} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// COMPETITOR BAR CHART
// Horizontal bar chart showing K9os leading each competitor by score. Pure
// tailwind + inline width % — no external chart lib so the page stays fast
// and matches the rest of the site's font/styling.
// ---------------------------------------------------------------------------
const CompetitorBarChart = () => {
  const MAX = 10;
  return (
    <Card className='p-6 sm:p-8 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800' data-testid='compare-bar-chart'>
      <div className='flex items-start justify-between gap-4 mb-6'>
        <div>
          <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E] mb-1'>
            <BarChart3 className='w-3.5 h-3.5' /> Head-to-head score
          </div>
          <h3 className='font-display text-2xl font-bold text-[#0B1220] dark:text-slate-50'>K9os vs the field</h3>
          <p className='text-sm text-slate-600 dark:text-slate-300 mt-1'>Scored 0-10 across scheduler, CRM, POS, memberships, contracts, messaging, marketing, and accounting.</p>
        </div>
      </div>
      <div className='space-y-3'>
        {/* K9os benchmark bar */}
        <div className='flex items-center gap-3'>
          <div className='w-40 shrink-0 text-sm font-bold text-[#0B1220] dark:text-slate-50'>
            <span className='inline-flex items-center gap-1.5'><Award className='w-3.5 h-3.5 text-[#C8102E]' /> K9os App</span>
          </div>
          <div className='flex-1 h-8 bg-slate-100 dark:bg-slate-800 rounded-md overflow-hidden relative'>
            <div
              className='h-full rounded-md bg-gradient-to-r from-[#1B3A87] via-[#1B3A87] to-[#C8102E] flex items-center justify-end pr-3 text-[11px] font-bold text-white shadow-inner'
              style={{ width: `${(K9OS_SCORE / MAX) * 100}%` }}
            >
              {K9OS_SCORE.toFixed(1)}
            </div>
          </div>
        </div>
        {/* Every competitor bar */}
        {COMPETITORS.map((c) => (
          <div key={c.slug} className='flex items-center gap-3'>
            <div className='w-40 shrink-0 text-sm text-slate-700 dark:text-slate-300 truncate' title={c.name}>{c.name}</div>
            <div className='flex-1 h-8 bg-slate-100 dark:bg-slate-800 rounded-md overflow-hidden relative'>
              <div
                className='h-full rounded-md bg-slate-400 dark:bg-slate-600 flex items-center justify-end pr-3 text-[11px] font-semibold text-white'
                style={{ width: `${(c.score / MAX) * 100}%` }}
              >
                {c.score.toFixed(1)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Head <script> JSON-LD injector — no external lib, cheap SEO win. Injects
// FAQPage schema so Google renders "Gingr alternative", "Time to Pet vs
// K9os", etc. as rich-result cards.
// ---------------------------------------------------------------------------
const injectComparisonSchema = () => {
  const existing = document.getElementById('compare-jsonld');
  if (existing) existing.remove();
  const faq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: COMPETITORS.slice(0, 8).map((c) => ({
      '@type': 'Question',
      name: `How does K9os compare to ${c.name}?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `K9os is a modern all-in-one dog training software for dog trainers, board-and-train facilities, and kennels. Where ${c.name} is known for ${c.strength.toLowerCase()}, K9os adds a per-client custom-built app, an AI-powered SEO blog engine, and regional SEO landing pages on top of core scheduling, CRM, POS, memberships, contracts, and messaging. See the full K9os vs ${c.name} comparison at https://k9os.app/compare.`,
      },
    })),
  };
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.id = 'compare-jsonld';
  s.text = JSON.stringify(faq);
  document.head.appendChild(s);
};

// ---------------------------------------------------------------------------
// PAGE
// ---------------------------------------------------------------------------
export default function Compare() {
  const [filter, setFilter] = useState('all');

  // Update tab title + meta description + schema on mount so the page
  // becomes a first-class SEO landing target.
  useEffect(() => {
    const brandList = COMPETITORS.map((c) => c.name).slice(0, 8).join(', ');
    document.title = `K9os vs Gingr, Time to Pet, DoTimely & more — Best Dog Training Software Comparison 2026`;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', `K9os App vs ${brandList} and more — see the full dog-training software comparison across scheduling, CRM, POS, memberships, contracts, messaging, and marketing. Book a free demo.`);
    setMeta('keywords', `dog training software comparison, Gingr alternative, Time to Pet alternative, DoTimely alternative, Kennel Connection alternative, PetExec alternative, K9 Bytes alternative, PawLoyalty alternative, ProPet alternative, best dog training software, board and train software, dog kennel software, dog daycare software, K9os vs Gingr, K9os vs Time to Pet`);
    setMeta('og:title', 'K9os vs Gingr, Time to Pet, DoTimely & more — Dog Training Software Comparison');
    setMeta('og:description', `Full head-to-head comparison of K9os vs ${brandList} across every feature dog trainers care about.`);
    setMeta('og:url', 'https://k9os.app/compare');
    setMeta('og:type', 'article');
    injectComparisonSchema();
    return () => {
      document.getElementById('compare-jsonld')?.remove();
    };
  }, []);

  // Filter competitors by category so the matrix stays browsable
  const filtered = useMemo(() => {
    if (filter === 'all') return COMPETITORS;
    if (filter === 'kennel') return COMPETITORS.filter((c) => ['gingr','revelation-pets','kennel-connection','k9-bytes','petexec','petlinx','propet','pawloyalty','kennel-booker'].includes(c.slug));
    if (filter === 'petsitter') return COMPETITORS.filter((c) => ['time-to-pet','dotimely','precise-petcare','scout','easy-busy-pets'].includes(c.slug));
    if (filter === 'pos') return COMPETITORS.filter((c) => ['square','gingr','petexec','pawloyalty','kennel-connection'].includes(c.slug));
    return COMPETITORS;
  }, [filter]);

  return (
    <div className='min-h-screen flex flex-col bg-white dark:bg-[#0B1220]' data-testid='page-compare'>
      <PublicNav />

      {/* Hero */}
      <section className='relative py-16 sm:py-24 bg-gradient-to-br from-[#0B1220] via-[#0F1A33] to-[#1B3A87] text-white overflow-hidden'>
        <div aria-hidden='true' className='pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#C8102E]/25 blur-3xl' />
        <div aria-hidden='true' className='pointer-events-none absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#93C5FD]/15 blur-3xl' />
        <div className='relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#F87171] mb-3'>
            <Filter className='w-3.5 h-3.5' /> Dog training software comparison &middot; 2026
          </div>
          <h1
            className='font-display text-3xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-tight'
            data-testid='compare-h1'
          >
            K9os vs Gingr vs Time to Pet vs DoTimely &mdash; the all-in-one dog training software comparison.
          </h1>
          <p className='mt-5 text-lg sm:text-xl text-slate-200 max-w-3xl leading-relaxed'>
            Trying to choose the best dog training software for your board-and-train facility, kennel, daycare, or dog-training company? Compare K9os head-to-head against every major competitor &mdash; Gingr, Revelation Pets, Square, Time to Pet, DoTimely, Kennel Connection, K9 Bytes, PetExec, PetLinx, ProPet, Precise Petcare, Scout, PawLoyalty, Kennel Booker, and Easy Busy Pets &mdash; across scheduling, CRM, POS, memberships, digital contracts, messaging, and marketing. K9os is white-label ready &mdash; anything a client needs, we can build.
          </p>
          <div className='mt-8 flex flex-wrap gap-3'>
            <a
              href='#matrix'
              className='inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white font-bold text-sm shadow-lg'
              data-testid='compare-cta-matrix'
            >
              Jump to feature matrix <ArrowRight className='w-4 h-4' />
            </a>
            <Link
              to='/contact'
              className='inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white font-bold text-sm hover:bg-white/20'
              data-testid='compare-cta-demo'
            >
              Book a free demo <ChevronRight className='w-4 h-4' />
            </Link>
          </div>
        </div>
      </section>

      {/* Bar chart */}
      <section className='py-14 sm:py-20 bg-slate-50 dark:bg-slate-950' id='chart'>
        <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8'>
          <CompetitorBarChart />
        </div>
      </section>

      {/* Feature matrix */}
      <section className='py-14 sm:py-20 bg-white dark:bg-[#0B1220]' id='matrix'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='max-w-3xl'>
            <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E] mb-3'>
              <Layers className='w-3.5 h-3.5' /> Feature parity matrix
            </div>
            <h2 className='font-display text-3xl sm:text-4xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight'>
              Six features that make K9os different from every other dog training software.
            </h2>
            <p className='mt-3 text-slate-600 dark:text-slate-300 text-base sm:text-lg'>
              Every competitor here has scheduling, CRM, POS, memberships, contracts, and messaging &mdash; that&rsquo;s table stakes, and the exact feature set for each vendor evolves regularly. What K9os uniquely adds on top: <strong>your own custom-built app</strong>, white-label extensions, an AI marketing engine, a native client PWA with push, and regional SEO landing pages for every U.S. state, Canada, the UK, and worldwide.
            </p>
          </div>

          <Tabs value={filter} onValueChange={setFilter} className='mt-8'>
            <TabsList className='bg-slate-100 dark:bg-slate-800 flex-wrap h-auto'>
              <TabsTrigger value='all' data-testid='filter-all'>All competitors</TabsTrigger>
              <TabsTrigger value='kennel' data-testid='filter-kennel'>Kennel &amp; board-and-train</TabsTrigger>
              <TabsTrigger value='petsitter' data-testid='filter-petsitter'>Pet-sitter &amp; walker apps</TabsTrigger>
              <TabsTrigger value='pos' data-testid='filter-pos'>POS &amp; payments</TabsTrigger>
            </TabsList>
            <TabsContent value={filter}>
              <div className='mt-6 overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-2xl'>
                <table className='min-w-full text-sm' data-testid='compare-matrix'>
                  <thead className='bg-slate-100 dark:bg-slate-900 text-left'>
                    <tr>
                      <th className='px-4 py-3 font-bold text-[#0B1220] dark:text-slate-50 sticky left-0 bg-slate-100 dark:bg-slate-900 z-10 min-w-[240px]'>Feature</th>
                      <th className='px-3 py-3 font-bold text-[#C8102E] text-center min-w-[90px]'>K9os</th>
                      {filtered.map((c) => (
                        <th key={c.slug} className='px-3 py-3 font-semibold text-slate-600 dark:text-slate-300 text-center whitespace-nowrap min-w-[110px]'>{c.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-slate-100 dark:divide-slate-800'>
                    {FEATURE_ROWS.map((row) => (
                      <tr key={row.key} className='bg-white dark:bg-slate-900 hover:bg-slate-50/60 dark:hover:bg-slate-800/60 transition-colors'>
                        <td className='px-4 py-3 sticky left-0 bg-inherit z-10'>
                          <div className='flex items-center gap-2'>
                            <row.icon className='w-4 h-4 text-[#1B3A87] dark:text-[#93C5FD] shrink-0' />
                            <span className='text-slate-800 dark:text-slate-100 font-medium'>{row.label}</span>
                          </div>
                        </td>
                        <td className='px-3 py-3'><FeatureCell state='yes' /></td>
                        {filtered.map((c) => (
                          <td key={c.slug} className='px-3 py-3'><FeatureCell state={featureFor(c, row.key)} /></td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className='mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-600 dark:text-slate-300'>
                <span className='inline-flex items-center gap-1.5'>
                  <span className='w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center'>
                    <Check className='w-2.5 h-2.5 text-white' strokeWidth={4} />
                  </span>
                  Included
                </span>
                <span className='inline-flex items-center gap-1.5'>
                  <span className='w-4 h-4 rounded-full bg-amber-400 flex items-center justify-center'>
                    <span className='w-1.5 h-1.5 rounded-full bg-white' />
                  </span>
                  Varies by plan / add-on
                </span>
                <span className='inline-flex items-center gap-1.5'>
                  <span className='w-4 h-4 rounded-full bg-rose-500 flex items-center justify-center'>
                    <X className='w-2.5 h-2.5 text-white' strokeWidth={4} />
                  </span>
                  Not standard in this product
                </span>
              </div>
              <p className='mt-3 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed'>
                Comparison based on each vendor&rsquo;s publicly documented features as of {new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })}. Product capabilities change frequently &mdash; check with each vendor directly for their latest offering. K9os holds no affiliation with the products listed here; all brand names are trademarks of their respective owners.
              </p>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Competitor deep-dive cards */}
      <section className='py-14 sm:py-20 bg-slate-50 dark:bg-slate-950'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='max-w-3xl'>
            <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E] mb-3'>
              <TrendingUp className='w-3.5 h-3.5' /> Alternatives breakdown
            </div>
            <h2 className='font-display text-3xl sm:text-4xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight'>
              Best K9os alternatives &mdash; and where each competitor falls short.
            </h2>
            <p className='mt-3 text-slate-600 dark:text-slate-300'>
              Every card summarises what the competitor is known for and how K9os compares. Written in positive K9os-first language &mdash; we don&rsquo;t use competitor logos or make categorical claims about what other products can or can&rsquo;t do. Always verify each vendor&rsquo;s current feature set directly on their site before making a purchase decision.
            </p>
          </div>

          <div className='mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
            {COMPETITORS.map((c) => (
              <Card
                key={c.slug}
                className='p-6 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-lg hover:-translate-y-0.5 transition-all'
                data-testid={`compare-card-${c.slug}`}
              >
                <div className='flex items-start justify-between gap-3'>
                  <div>
                    <div className='font-display text-lg font-bold text-[#0B1220] dark:text-slate-50'>K9os vs {c.name}</div>
                    <div className='text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mt-0.5'>{c.tagline}</div>
                  </div>
                  <Badge className='bg-[#1B3A87]/10 text-[#1B3A87] dark:bg-[#93C5FD]/15 dark:text-[#93C5FD] border-0 shrink-0'>Compare</Badge>
                </div>
                <div className='mt-4 space-y-3 text-sm'>
                  <div>
                    <div className='text-[10px] uppercase tracking-wider font-bold text-emerald-600 dark:text-emerald-400 mb-1'>{c.name} is known for</div>
                    <div className='text-slate-700 dark:text-slate-300'>{c.strength}</div>
                  </div>
                  <div>
                    <div className='text-[10px] uppercase tracking-wider font-bold text-[#C8102E] dark:text-[#F87171] mb-1'>How K9os compares</div>
                    <div className='text-slate-700 dark:text-slate-300'>{c.weakness}</div>
                  </div>
                </div>
                <div className='mt-5 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs'>
                  <span className='text-slate-500 dark:text-slate-400'>K9os score</span>
                  <span className='font-bold text-[#C8102E]'>{K9OS_SCORE.toFixed(1)} / 10</span>
                  <span className='text-slate-500 dark:text-slate-400'>{c.name}</span>
                  <span className='font-bold text-slate-700 dark:text-slate-200'>{c.score.toFixed(1)} / 10</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Lead form — same one used on every regional landing */}
      <DemoLeadSection
        regionName='the World'
        regionSlug='compare'
        eyebrow='See K9os in action'
        headingOverride='Ready to replace Gingr, Time to Pet, DoTimely &amp; friends with one all-in-one system?'
      />

      <MessageGregBubble />
    </div>
  );
}
